<template>
  <Teleport
    defer
    :to="teleportTo"
  >
    <div
      ref="rootRef"
      class="tutorial"
      :class="classes"
      :style="style"
    >
      <div
        class="tutorial__wrapper"
        :class="arrowModifierClass"
        :style="arrowOffsetStyle"
      >
        <div class="tutorial__header">
          <ui-icon v-if="headerIcon">{{ headerIcon }}</ui-icon>
          <slot
            name="tutorial-header"
            :current-step="currentStep"
          ></slot>
        </div>
        <!-- tutorial__header -->
        <img
          v-if="image"
          :src="image"
          alt=""
          class="tutorial__image"
        />
        <div class="tutorial__content">
          <slot :current-step="currentStep"></slot>
        </div>
        <!-- tutorial__content -->

        <div class="tutorial__footer">
          <slot name="tutorial-footer">
            <div class="tutorial__counter">
              <span v-if="totalSteps > 1">{{ stepCounter }}</span>
            </div>
            <div class="tutorial__footer-controls">
              <AcvButton
                v-if="isPreviousButtonVisible"
                variant="link"
                @click="decreaseStep"
              >
                <span class="tutorial__footer-button">Previous</span>
              </AcvButton>
              <AcvButton
                variant="link"
                @click="increaseStep"
              >
                <span class="tutorial__footer-button">
                  {{ nextButtonLabel }}
                </span>
              </AcvButton>
            </div>
          </slot>
        </div>
        <!-- tutorial__footer -->
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { AcvButton } from '@acv/shared-vue';

const props = defineProps({
  teleportTo: {
    type: String,
    default: 'body',
  },
  totalSteps: {
    type: Number,
    default: 1,
  },
  classes: {
    type: String,
    default: '',
  },
  style: {
    type: [String, Object, Array],
    default: '',
  },
  enableArrow: {
    type: Boolean,
    default: true,
  },
  /**
   * Which edge of the tooltip the arrow renders on — the side facing
   * whatever the tooltip is pointing at. Callers that don't track a target's
   * on-screen position (everything except the Walkthrough Controller) can
   * leave this at its default.
   */
  arrowDirection: {
    type: String,
    default: 'top',
    validator: (value) => ['top', 'bottom', 'left', 'right'].includes(value),
  },
  /**
   * Where the arrow sits along the edge facing the target, as a CSS length (e.g. `'42px'`).
   * Only meaningful for callers that position this tooltip themselves and may have had to
   * pull it back inside the viewport, leaving the tooltip's centre no longer aligned with
   * whatever it points at. Left empty, the arrow stays centred on its edge as before.
   */
  arrowOffset: {
    type: String,
    default: '',
  },
  headerIcon: {
    type: String,
    default: '',
  },
  image: {
    type: String,
    default: '',
  },
  allowStepBack: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['onFinish']);

/**
 * Track the current step and either increase or decrease
 */
const useFooterControls = () => {
  const { totalSteps } = props;
  const currentStep = ref(1);

  const increaseStep = () => {
    if (currentStep.value === totalSteps) {
      emit('onFinish');
      return;
    }
    currentStep.value++;
  };

  const decreaseStep = () => {
    if (currentStep.value === 1) {
      return;
    }
    currentStep.value--;
  };

  return {
    increaseStep,
    decreaseStep,
    currentStep,
  };
};

/**
 * Handles step counter and button text
 */
const useTutorialLabels = (currentStep) => {
  const { totalSteps, allowStepBack } = props;
  const stepCounter = computed(() => `${currentStep.value} of ${totalSteps}`);

  const nextButtonLabel = computed(() => {
    let text = 'Got it!';

    if (currentStep.value < totalSteps) {
      text = 'Next';
    } else if (currentStep.value === totalSteps) {
      text = 'Got it!';
    }

    return text;
  });

  const isPreviousButtonVisible = computed(() => allowStepBack && currentStep.value > 1);

  return {
    stepCounter,
    nextButtonLabel,
    isPreviousButtonVisible,
  };
};

const { increaseStep, decreaseStep, currentStep } = useFooterControls();
const { stepCounter, nextButtonLabel, isPreviousButtonVisible } = useTutorialLabels(currentStep);

const arrowModifierClass = computed(() =>
  props.enableArrow ? `tutorial__wrapper--arrow-${props.arrowDirection}` : '',
);

/**
 * Fed to the arrow's own CSS as a custom property so the stylesheet keeps its 50% default
 * for every caller that doesn't position this tooltip itself.
 */
const arrowOffsetStyle = computed(() =>
  props.arrowOffset ? { '--tutorial-arrow-offset': props.arrowOffset } : undefined,
);

/**
 * Exposed so a caller that positions this tooltip can measure it — clamping a tooltip
 * inside the viewport is impossible without knowing how big it is. Note attrs don't cross
 * the Teleport boundary (the same reason `style` is a real prop), but a component `ref` does.
 */
const rootRef = ref(null);

defineExpose({ rootRef });
</script>

<style lang="scss" scoped>
$tutorial-border-color: #c0daeb;

.tutorial {
  @apply tw-absolute tw-min-w-[280px] tw-max-w-[360px];

  .tutorial__wrapper {
    @apply tw-relative tw-rounded-lg tw-bg-white tw-p-4 tw-shadow-lg;
    border: 1px solid $tutorial-border-color;

    &--arrow-top::before,
    &--arrow-bottom::before,
    &--arrow-left::before,
    &--arrow-right::before,
    &--arrow-top::after,
    &--arrow-bottom::after,
    &--arrow-left::after,
    &--arrow-right::after {
      content: '';
      position: absolute;
      width: 0;
      height: 0;
    }

    &--arrow-top::before,
    &--arrow-bottom::before,
    &--arrow-left::before,
    &--arrow-right::before {
      border: 9px solid transparent;
    }

    &--arrow-top::after,
    &--arrow-bottom::after,
    &--arrow-left::after,
    &--arrow-right::after {
      border: 8px solid transparent;
    }

    &--arrow-top::before {
      top: -18px;
      left: var(--tutorial-arrow-offset, 50%);
      margin-left: -9px;
      border-bottom-color: $tutorial-border-color;
    }

    &--arrow-top::after {
      top: -16px;
      left: var(--tutorial-arrow-offset, 50%);
      margin-left: -8px;
      border-bottom-color: white;
    }

    &--arrow-bottom::before {
      bottom: -18px;
      left: var(--tutorial-arrow-offset, 50%);
      margin-left: -9px;
      border-top-color: $tutorial-border-color;
    }

    &--arrow-bottom::after {
      bottom: -16px;
      left: var(--tutorial-arrow-offset, 50%);
      margin-left: -8px;
      border-top-color: white;
    }

    &--arrow-left::before {
      left: -18px;
      top: var(--tutorial-arrow-offset, 50%);
      margin-top: -9px;
      border-right-color: $tutorial-border-color;
    }

    &--arrow-left::after {
      left: -16px;
      top: var(--tutorial-arrow-offset, 50%);
      margin-top: -8px;
      border-right-color: white;
    }

    &--arrow-right::before {
      right: -17px;
      top: var(--tutorial-arrow-offset, 50%);
      margin-top: -9px;
      border-left-color: $tutorial-border-color;
    }

    &--arrow-right::after {
      right: -15px;
      top: var(--tutorial-arrow-offset, 50%);
      margin-top: -8px;
      border-left-color: white;
    }
  }

  .tutorial__header {
    @apply tw-flex tw-items-center tw-gap-2 tw-pb-1 tw-text-base tw-font-medium tw-text-black-870;
  }

  .tutorial__image {
    @apply tw-mb-1 tw-w-full tw-rounded;
  }

  .tutorial__content {
    @apply tw-w-full tw-py-1 tw-text-left tw-text-sm tw-text-black-670;
  }

  .tutorial__footer {
    @apply tw-flex tw-w-full tw-items-center tw-justify-between tw-pt-1 tw-text-xs tw-text-black-670;

    .tutorial__footer-controls {
      @apply tw-flex tw-items-center tw-gap-4;
    }

    .tutorial__footer-button {
      @apply tw-rounded tw-p-1 tw-font-sans tw-text-sm tw-font-bold tw-text-secondary-base;
      transition: background-color 0.25s ease-in-out;

      &:hover {
        @apply tw-bg-grey-light;
      }
    }
  }
}
</style>
